import {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { t } from "../locale";
import { initializeAnalytics, resetAnalytics } from "../portal/analytics";
import { deviceRequest, ensureDeviceAccount, resetDeviceAccount, ViewerError } from "./device-session";
import EmailLogin, { type LoginReason, type LoginResult } from "./EmailLogin";
import PostRegistration from "./PostRegistration";
import "./email-login.css";
export type Viewer = {
  userId: string;
  accountType: "device" | "email" | "registered";
  registered: boolean;
  email?: string;
  emailVerified?: boolean;
  deviceId: string;
  devicePlatform: string;
  nickname: string;
  avatar: string;
  enabled: boolean;
  revision: number;
  createdAt: string;
  lastSeenAt?: string;
};
export type LibraryItem = {
  seriesId: string;
  title: string;
  saved: boolean;
  progress: { episode: number; seconds: number; updatedAt: number } | null;
};
type Change = {
  seriesId: string;
  title: string;
  saved?: boolean;
  progress?: { episode: number; seconds: number } | null;
};
type Library = { revision: number; items: LibraryItem[] };
type ViewerContextType = {
  user: Viewer | null;
  ready: boolean;
  error: string;
  guestId: string;
  items: LibraryItem[];
  refresh: () => Promise<void>;
  updateProfile: (nickname: string, avatar: string) => Promise<void>;
  changeItem: (change: Change) => Promise<void>;
  requestLogin: (reason?: LoginReason) => Promise<boolean>;
  offerRegistration: () => void;
  logout: () => Promise<void>;
};
const Context = createContext<ViewerContextType | null>(null);
export const useViewer = () => {
  const value = useContext(Context);
  if (!value) throw new Error("ViewerProvider required");
  return value;
};
export function ViewerProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<Viewer | null>(null);
  const [ready, setReady] = useState(false);
  const [error, setError] = useState("");
  const [items, setItems] = useState<LibraryItem[]>([]);
  const [loginReason, setLoginReason] = useState<LoginReason | null>(null);
  const [registeredNotice, setRegisteredNotice] = useState(false);
  const loginWaiters = useRef<((value: boolean) => void)[]>([]);
  const authChannel = useRef<BroadcastChannel | null>(null);
  const offered = useRef(false);
  const revision = useRef(0);
  const owner = useRef("");
  const mounted = useRef(false);
  const refreshTask = useRef<Promise<void> | null>(null);
  const queue = useRef<Promise<void>>(Promise.resolve());
  function applyLibrary(data: Library) {
    if (data.revision >= revision.current) {
      revision.current = data.revision;
      setItems(data.items);
    }
  }
  function fail(error: unknown) {
    setError(
      error instanceof Error
        ? error.message
        : t("Could not connect to your account."),
    );
    setUser(null);
    setItems([]);
    revision.current = 0;
  }
  function refresh(recovered = false): Promise<void> {
    if (refreshTask.current) return refreshTask.current;
    refreshTask.current = (async () => {
      try {
        await ensureDeviceAccount();
        const profile = await deviceRequest<Viewer>("account/me");
        // Never attach old client data to a new account after storage loss.
        if (owner.current !== profile.userId) {
          revision.current = 0;
          setItems([]);
        }
        owner.current = profile.userId;
        const library = await deviceRequest<Library>("library");
        if (!mounted.current) return;
        setUser(profile);
        applyLibrary(library);
        setError("");
      } catch (error) {
        if (!recovered && error instanceof ViewerError && error.status === 401) {
          resetDeviceAccount(); resetAnalytics();
          if (mounted.current) { setUser(null); setItems([]); }
          refreshTask.current = null;
          return await refresh(true);
        }
        if (mounted.current) fail(error);
      } finally {
        if (mounted.current) setReady(true);
        refreshTask.current = null;
      }
    })();
    return refreshTask.current;
  }
  useEffect(() => {
    mounted.current = true;
    void refresh().then(() => {
      void initializeAnalytics();
    });
    const focus = () => {
      void refresh();
    };
    addEventListener("focus", focus);
    addEventListener("online", focus);
    try { authChannel.current = new BroadcastChannel("yestv-account"); authChannel.current.onmessage = () => { resetDeviceAccount(); resetAnalytics(); void refresh(); }; } catch { /* Focus refresh remains available. */ }
    return () => {
      mounted.current = false;
      removeEventListener("focus", focus);
      removeEventListener("online", focus);
      authChannel.current?.close();
    };
  }, []);
  function finishLogin(value: boolean) { setLoginReason(null); const pending = loginWaiters.current.splice(0); pending.forEach(resolve => resolve(value)); }
  function requestLogin(reason: LoginReason = "login"): Promise<boolean> {
    if (user?.registered) return Promise.resolve(true);
    if (window.top !== window.self) { window.parent.postMessage({ type: "yestv:account", action: "login", reason }, location.origin); return Promise.resolve(false); }
    setLoginReason(current => current || reason);
    return new Promise(resolve => loginWaiters.current.push(resolve));
  }
  function offerRegistration() {
    if (user?.registered || loginReason || offered.current) return;
    offered.current = true;
    const key = `yestv:registration-offered:${user?.userId || "guest"}`;
    try { if (sessionStorage.getItem(key)) return; sessionStorage.setItem(key, "1"); } catch { /* Once per mounted viewer fallback. */ }
    void requestLogin("trial");
  }
  async function signedIn(result: LoginResult) {
    resetDeviceAccount(); if (refreshTask.current) await refreshTask.current; await refresh();
    finishLogin(true); authChannel.current?.postMessage("signed-in");
    if (result.newRegistration) setRegisteredNotice(true);
  }
  async function logout() {
    await deviceRequest("account/logout", "POST", {});
    resetDeviceAccount(); resetAnalytics(); setRegisteredNotice(false);
    authChannel.current?.postMessage("signed-out");
    if (refreshTask.current) await refreshTask.current; await refresh(); await initializeAnalytics();
  }
  async function updateProfile(nickname: string, avatar: string) {
    if (!user) throw new Error(t("Could not connect to your account."));
    const profile = await deviceRequest<Viewer>("account/me", "PUT", {
      nickname,
      avatar,
      expectedRevision: user.revision,
    });
    if (mounted.current) {
      setUser(profile);
      setError("");
    }
  }
  async function changeItem(change: Change): Promise<void> {
    if (!user)
      return Promise.reject(new Error(t("Could not connect to your account.")));
    if (change.saved === true && !user.registered && !await requestLogin("save")) throw new Error("已取消收藏，请先登录。");
    const expectedOwner = owner.current;
    const action = queue.current
      .catch(() => {})
      .then(async () => {
        if (expectedOwner !== owner.current)
          throw new Error(t("Account environment mismatch."));
        try {
          const next = await deviceRequest<Library>("library", "PUT", change);
          if (mounted.current && expectedOwner === owner.current) {
            applyLibrary(next);
            setError("");
          }
        } catch (error) {
          if (mounted.current)
            setError(
              t(
                "Your latest change could not be synced. Please retry when connected.",
              ),
            );
          throw error;
        }
      });
    queue.current = action;
    return action;
  }
  return (
    <Context.Provider
      value={{
        user,
        ready,
        error,
        guestId: user?.userId || "",
        items,
        refresh,
        updateProfile,
        changeItem,
        requestLogin,
        offerRegistration,
        logout,
      }}
    >
      {user ? (
        <>{children}{loginReason && <EmailLogin reason={loginReason} onClose={() => finishLogin(false)} onSuccess={signedIn} />}{registeredNotice && <PostRegistration onClose={() => setRegisteredNotice(false)} />}</>
      ) : (
        <main className="device-account-gate" aria-live="polite">
          <a href="/" className="wordmark">
            yes<span>tv</span>
            <i>.</i>
          </a>
          <h1>
            {t(
              ready
                ? "Account connection unavailable"
                : "Preparing your account…",
            )}
          </h1>
          <p>
            {error ||
              t(
                "正在准备观看页面，你可以先以游客身份试看。",
              )}
          </p>
          {ready && (
            <button
              className="account-primary"
              onClick={() => {
                setReady(false);
                void refresh();
              }}
            >
              {t("Retry connection")}
            </button>
          )}
        </main>
      )}
    </Context.Provider>
  );
}
